/**
 * AgentDetailQualitaet.jsx
 * Block 4 — Qualität:
 *   FCR Rate (letzte 30 Tage bis vor 3 Tagen, 24h Cache)
 *   Weiterleitungen (aktueller Zeitraum)
 *   Nicht-FCR-Tickets nach Ziel-Abteilung (Drilldown mit Verlauf)
 */
import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:3001';

// ─── Konstanten fuer Timeline (eigenstaendig, keine Abhaengigkeit zu anderen Dateien) ──
const STATUS_LABELS = {
  new: 'Neu', open: 'Offen', pending: 'Wartend',
  solved: 'Gelöst', closed: 'Geschlossen',
};
const STATUS_COLORS = {
  new: 'status--new', open: 'status--open', pending: 'status--pending',
  solved: 'status--solved', closed: 'status--closed',
};
const EVENT_CONFIG = {
  created:     { color: '#185FA5', bg: '#E6F1FB', label: 'Erstellt',      icon: '●' },
  status:      { color: '#854F0B', bg: '#FAEEDA', label: 'Status',        icon: '◆' },
  assignee_id: { color: '#3B6D11', bg: '#EAF3DE', label: 'Übernahme',     icon: '▲' },
  group_id:    { color: '#993556', bg: '#FBEAF0', label: 'Weiterleitung', icon: '→' },
};

function fmtDateTime(iso) {
  if (!iso) return '–';
  return new Date(iso).toLocaleString('de-CH', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Timeline-Render (eigenstaendige Kopie — aendert nichts an bestehenden Komponenten)
function TicketTimeline({ ticket, auditData, loading }) {
  if (loading) return (
    <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--color-text-secondary)' }}>
      Verlauf wird geladen…
    </div>
  );
  const history = auditData?.history || [];
  const events = [
    ticket.created_at ? { type: 'created', timestamp: ticket.created_at, kanal: ticket.kanal } : null,
    ...history.map(h => ({ ...h, type: h.field })),
  ].filter(Boolean).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const groupEvents = history.filter(h => h.field === 'group_id');
  const routingPath = [];
  if (groupEvents.length > 0) {
    if (groupEvents[0].fromGroup) routingPath.push({ name: groupEvents[0].fromGroup, isCurrent: false });
    groupEvents.forEach((ev, i) => {
      if (ev.toGroup) routingPath.push({ name: ev.toGroup, isCurrent: i === groupEvents.length - 1 });
    });
  }

  return (
    <div style={{ padding: '12px 16px', background: 'var(--color-background-secondary)', borderBottom: '1px solid var(--color-border-tertiary)' }}>
      {routingPath.length > 1 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: 'var(--color-text-secondary)', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Routing-Pfad</div>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
            {routingPath.map((step, i) => (
              <React.Fragment key={i}>
                <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 500, background: step.isCurrent ? '#EAF3DE' : '#F1EFE8', color: step.isCurrent ? '#3B6D11' : '#5F5E5A', border: step.isCurrent ? '1px solid #97C459' : '0.5px solid rgba(0,0,0,0.15)' }}>{step.name}</span>
                {i < routingPath.length - 1 && <span style={{ color: 'var(--color-text-secondary)', fontSize: 12 }}>→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 14, top: 8, bottom: 8, width: 1, background: 'rgba(0,0,0,0.08)' }} />
        {events.length === 0 && <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', paddingLeft: 40 }}>Kein Verlauf verfügbar</div>}
        {events.map((ev, i) => {
          const cfg = EVENT_CONFIG[ev.type] || EVENT_CONFIG.status;
          return (
            <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, position: 'relative' }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: cfg.bg, color: cfg.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, zIndex: 1, border: `1px solid ${cfg.color}33` }}>{cfg.icon}</div>
              <div style={{ paddingTop: 4, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 500, padding: '1px 6px', borderRadius: 99, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{fmtDateTime(ev.timestamp)}</span>
                </div>
                <div style={{ fontSize: 12, marginTop: 3, lineHeight: 1.5 }}>
                  {ev.type === 'created' && <>Ticket erstellt <span style={{ color: 'var(--color-text-secondary)' }}>— {ev.kanal === 'telefon' ? 'Telefonanruf' : ev.kanal === 'mail' ? 'E-Mail' : ev.kanal === 'web' ? 'Web' : 'unbekannt'}</span></>}
                  {ev.type === 'status' && <>{ev.previousValue ? <><span style={{ color: 'var(--color-text-secondary)' }}>{STATUS_LABELS[ev.previousValue] || ev.previousValue}</span> → </> : ''}<strong style={{ fontWeight: 500 }}>{STATUS_LABELS[ev.value] || ev.value}</strong></>}
                  {ev.type === 'assignee_id' && <>Übernommen von <strong style={{ fontWeight: 500 }}>{ev.toAgent || '–'}</strong>{ev.fromAgent ? <span style={{ color: 'var(--color-text-secondary)' }}> (vorher: {ev.fromAgent})</span> : ''}</>}
                  {ev.type === 'group_id' && <>Weitergeleitet{ev.fromGroup ? <> von <strong style={{ fontWeight: 500 }}>{ev.fromGroup}</strong></> : ''} → <strong style={{ fontWeight: 500, color: '#993556' }}>{ev.toGroup || '–'}</strong></>}
                  {ev.byAgentName && ev.type !== 'assignee_id' && <span style={{ color: 'var(--color-text-secondary)', marginLeft: 6, fontSize: 11 }}>durch {ev.byAgentName}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AgentDetailQualitaet({ data, loading, agentId }) {
  const [fcrData, setFcrData]       = useState(null);
  const [fcrLoading, setFcrLoading] = useState(false);
  const [fcrError, setFcrError]     = useState(null);
  const [showFcrList, setShowFcrList] = useState(false);
  // Set von gruppeIds deren Drilldown gerade offen ist
  const [openZiele, setOpenZiele] = useState(new Set());

  // Nicht-FCR-Block State
  const [showNichtFcr, setShowNichtFcr]       = useState(false);
  const [openNichtFcrZiele, setOpenNichtFcrZiele] = useState(new Set());
  // Weiterleitungen-Block — Toggle analog zu FCR (Tabelle nur auf Klick)
  const [showWeiterleitungen, setShowWeiterleitungen] = useState(false);
  const [tlExpanded, setTlExpanded]           = useState(null);   // Ticket-ID mit offenem Verlauf
  const [tlCache, setTlCache]                 = useState({});     // ticketId -> timeline-data
  const [tlLoading, setTlLoading]             = useState({});

  // Reset Drilldowns wenn Agent oder Zeitraum (data) wechselt
  useEffect(() => {
    setOpenZiele(new Set());
    setOpenNichtFcrZiele(new Set());
    setTlExpanded(null);
    setShowWeiterleitungen(false);
  }, [data]);

  // Timeline (Verlauf) lazy laden — gleicher Endpoint wie Haupt-Ticketliste
  const handleTimelineToggle = useCallback(async (ticketId) => {
    if (tlExpanded === ticketId) { setTlExpanded(null); return; }
    setTlExpanded(ticketId);
    if (!ticketId || tlCache[ticketId]) return;
    setTlLoading(prev => ({ ...prev, [ticketId]: true }));
    try {
      const res  = await fetch(`${API_BASE}/api/tickets/${ticketId}/timeline`, { credentials: 'include' });
      const dta  = await res.json();
      setTlCache(prev => ({ ...prev, [ticketId]: dta }));
    } catch {
      setTlCache(prev => ({ ...prev, [ticketId]: { history: [] } }));
    } finally {
      setTlLoading(prev => ({ ...prev, [ticketId]: false }));
    }
  }, [tlExpanded, tlCache]);

  useEffect(() => {
    if (!agentId) return;
    setFcrData(null);
    setShowFcrList(false);
    // 2 Sekunden warten damit andere Blocks zuerst laden
    const timer = setTimeout(() => {
      setFcrLoading(true);
      setFcrError(null);
      fetch(`${API_BASE}/api/agent-detail/${agentId}/fcr`, { credentials: 'include' })
        .then(r => r.json())
        .then(d => { setFcrData(d); setFcrLoading(false); })
        .catch(() => { setFcrError('Fehler beim Laden'); setFcrLoading(false); });
    }, 2000);
    return () => clearTimeout(timer);
  }, [agentId]);

  function fmtDate(iso) {
    if (!iso) return '–';
    return new Date(iso).toLocaleString('de-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  if (loading) {
    return <div className="qualitaet-loading">Qualitätsdaten werden geladen…</div>;
  }
  if (!data) return null;

  const fcrRate  = fcrData?.fcrRate ?? null;
  const fcrColor = fcrRate === null ? '' :
    fcrRate >= 80 ? 'metric--green' :
    fcrRate >= 60 ? 'metric--amber' : 'metric--red';

  return (
    <div className="qualitaet-block">
      <div className="qualitaet-metrics">

        {/* FCR Rate — bewusst UNABHAENGIG vom oben gewaehlten Zeitraum (KW/Woche/
            Monat): rollierendes 30-Tage-Fenster bis vor 3 Tagen (Reopen-Puffer).
            Klar beschriften, sonst wirkt es so als gehoere die Zahl zur
            gewaehlten Woche/KW — tut sie nicht. */}
        <div className={`qualitaet-metric ${fcrRate !== null ? fcrColor : ''}`}>
          <span className="metric-label" title="Rollierendes 30-Tage-Fenster bis vor 3 Tagen — unabhängig vom oben gewählten Zeitraum">
            FCR Rate <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>(Ø 30 Tage)</span>
          </span>
          {fcrLoading && <span className="metric-value" style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Wird berechnet…</span>}
          {fcrError  && <span className="metric-value" style={{ fontSize: 14, color: 'var(--color-error)' }}>{fcrError}</span>}
          {!fcrLoading && !fcrError && fcrRate !== null && (
            <>
              <span className="metric-value">{fcrRate}%</span>
              <span className="metric-sub">{fcrData.fcrAnzahl} von {fcrData.gesamt} Tickets direkt gelöst</span>
              <span className="metric-sub" style={{ fontSize: 11 }}>{fcrData.zeitraumVon} – {fcrData.zeitraumBis} · unabhängig vom gewählten Zeitraum oben</span>
              {Array.isArray(fcrData.fcrTickets) && fcrData.fcrTickets.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowFcrList(s => !s)}
                  className="fcr-toggle"
                  style={{
                    marginTop: 6, fontSize: 11, background: 'none', border: 'none',
                    color: 'var(--color-link, #185FA5)', cursor: 'pointer', padding: 0,
                  }}
                >
                  {showFcrList ? '▾ Tickets ausblenden' : `▸ ${fcrData.fcrTickets.length} FCR-Tickets anzeigen`}
                </button>
              )}
              {Array.isArray(fcrData.nichtFcrDetails) && fcrData.nichtFcrDetails.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowNichtFcr(s => !s)}
                  className="fcr-toggle"
                  style={{
                    marginTop: 4, fontSize: 11, background: 'none', border: 'none',
                    color: 'var(--color-link, #185FA5)', cursor: 'pointer', padding: 0,
                  }}
                >
                  {showNichtFcr ? '▾ Nicht-FCR ausblenden' : `▸ ${fcrData.nichtFCR} Nicht-FCR-Tickets anzeigen`}
                </button>
              )}
            </>
          )}
          {!fcrLoading && !fcrError && fcrRate === null && fcrData !== null && (
            <span className="metric-value" style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Keine Daten</span>
          )}
        </div>

        {/* Weiterleitungsrate */}
        <div className="qualitaet-metric">
          <span className="metric-label">Weiterleitungen</span>
          <span className="metric-value">{data.weiterleitungenGesamt}</span>
          <span className="metric-sub">{data.weiterleitungRate}% aller Tickets</span>
          {Array.isArray(data.weiterleitungDetails) && data.weiterleitungDetails.length > 0 && (
            <button
              type="button"
              onClick={() => setShowWeiterleitungen(s => !s)}
              className="fcr-toggle"
              style={{
                marginTop: 6, fontSize: 11, background: 'none', border: 'none',
                color: 'var(--color-link, #185FA5)', cursor: 'pointer', padding: 0,
              }}
            >
              {showWeiterleitungen
                ? '▾ Weiterleitungen ausblenden'
                : `▸ ${data.weiterleitungenGesamt} Weiterleitungen anzeigen`}
            </button>
          )}
        </div>

      </div>

      {/* FCR-Tickets Drilldown */}
      {showFcrList && fcrData && Array.isArray(fcrData.fcrTickets) && fcrData.fcrTickets.length > 0 && (
        <div className="fcr-tickets-detail" style={{ marginTop: 12 }}>
          <h4>FCR-Tickets ({fcrData.fcrTickets.length})</h4>
          <table className="weiterleitungen-table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Produkt</th>
                <th>Erstellt</th>
                <th>Bearbeitungszeit</th>
              </tr>
            </thead>
            <tbody>
              {fcrData.fcrTickets.map(t => (
                <tr key={t.id}>
                  <td>
                    {t.zendeskUrl ? (
                      <a href={t.zendeskUrl} target="_blank" rel="noopener noreferrer">
                        #{t.id}
                      </a>
                    ) : `#${t.id}`}
                    <span style={{ marginLeft: 8, color: 'var(--color-text-secondary)' }}>{t.subject}</span>
                  </td>
                  <td>{t.produkt || '–'}</td>
                  <td>{fmtDate(t.createdAt)}</td>
                  <td>{t.bearbeitungFormatiert || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Nicht-FCR-Tickets nach Ziel-Abteilung mit Verlauf-Drilldown */}
      {showNichtFcr && fcrData && Array.isArray(fcrData.nichtFcrDetails) && fcrData.nichtFcrDetails.length > 0 && (
        <div className="weiterleitungen-detail" style={{ marginTop: 12 }}>
          <h4>Nicht-FCR-Tickets ({fcrData.nichtFCR}) — nach Ziel-Abteilung</h4>
          <table className="weiterleitungen-table">
            <thead>
              <tr>
                <th>Ziel-Abteilung</th>
                <th>Anzahl</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {fcrData.nichtFcrDetails.map((d) => {
                const isOpen = openNichtFcrZiele.has(d.gruppeId);
                const hasTickets = Array.isArray(d.tickets) && d.tickets.length > 0;
                return (
                  <React.Fragment key={d.gruppeId}>
                    <tr>
                      <td>{d.gruppeName}</td>
                      <td>{d.anzahl}</td>
                      <td>
                        {hasTickets && (
                          <button
                            type="button"
                            className="fcr-toggle"
                            style={{
                              fontSize: 11, background: 'none', border: 'none',
                              color: 'var(--color-link, #185FA5)', cursor: 'pointer', padding: 0,
                            }}
                            onClick={() => {
                              setOpenNichtFcrZiele(prev => {
                                const next = new Set(prev);
                                if (next.has(d.gruppeId)) next.delete(d.gruppeId);
                                else next.add(d.gruppeId);
                                return next;
                              });
                            }}
                          >
                            {isOpen ? '▾ Tickets ausblenden' : `▸ ${d.tickets.length} Tickets anzeigen`}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && hasTickets && (
                      <tr>
                        <td colSpan={3} style={{ padding: 0, background: 'var(--color-bg-soft, #f9fafb)' }}>
                          <table className="weiterleitungen-table" style={{ margin: 0 }}>
                            <thead>
                              <tr>
                                <th>ID</th>
                                <th>Erstellt</th>
                                <th>Betreff</th>
                                <th>Produkt</th>
                                <th>Status</th>
                                <th>Verlauf</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.tickets.map(t => (
                                <React.Fragment key={t.id}>
                                  <tr>
                                    <td>
                                      {t.zendeskUrl ? (
                                        <a href={t.zendeskUrl} target="_blank" rel="noopener noreferrer">#{t.id}</a>
                                      ) : `#${t.id}`}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(t.createdAt)}</td>
                                    <td>{t.subject}</td>
                                    <td>{t.produkt || '–'}</td>
                                    <td>
                                      <span className={`status-badge ${STATUS_COLORS[t.status] || ''}`}>
                                        {STATUS_LABELS[t.status] || t.status}
                                      </span>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                      <button
                                        type="button"
                                        className="fcr-toggle"
                                        style={{
                                          fontSize: 11, background: 'none', border: 'none',
                                          color: 'var(--color-link, #185FA5)', cursor: 'pointer', padding: 0,
                                        }}
                                        onClick={() => handleTimelineToggle(t.id)}
                                      >
                                        {tlExpanded === t.id ? '▾ Schliessen' : '▸ Verlauf'}
                                      </button>
                                    </td>
                                  </tr>
                                  {tlExpanded === t.id && (
                                    <tr>
                                      <td colSpan={6} style={{ padding: 0 }}>
                                        <TicketTimeline
                                          ticket={{ created_at: t.createdAt, kanal: t.kanal || 'unbekannt' }}
                                          auditData={tlCache[t.id]}
                                          loading={!!tlLoading[t.id]}
                                        />
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Weiterleitungen nach Ziel mit Drilldown */}
      {showWeiterleitungen && data.weiterleitungDetails && data.weiterleitungDetails.length > 0 && (
        <div className="weiterleitungen-detail">
          <h4>Weiterleitungsziele</h4>
          <table className="weiterleitungen-table">
            <thead>
              <tr>
                <th>Ziel-Abteilung</th>
                <th>Anzahl</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.weiterleitungDetails.map((d) => {
                const isOpen = openZiele.has(d.gruppeId);
                const hasTickets = Array.isArray(d.tickets) && d.tickets.length > 0;
                return (
                  <React.Fragment key={d.gruppeId}>
                    <tr>
                      <td>{d.gruppeName}</td>
                      <td>{d.anzahl}</td>
                      <td>
                        {hasTickets && (
                          <button
                            type="button"
                            className="fcr-toggle"
                            style={{
                              fontSize: 11, background: 'none', border: 'none',
                              color: 'var(--color-link, #185FA5)', cursor: 'pointer', padding: 0,
                            }}
                            onClick={() => {
                              setOpenZiele(prev => {
                                const next = new Set(prev);
                                if (next.has(d.gruppeId)) next.delete(d.gruppeId);
                                else next.add(d.gruppeId);
                                return next;
                              });
                            }}
                          >
                            {isOpen ? '▾ Tickets ausblenden' : `▸ ${d.tickets.length} Tickets anzeigen`}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && hasTickets && (
                      <tr>
                        <td colSpan={3} style={{ padding: 0, background: 'var(--color-bg-soft, #f9fafb)' }}>
                          <table className="weiterleitungen-table" style={{ margin: 0 }}>
                            <thead>
                              <tr>
                                <th>Ticket</th>
                                <th>Produkt</th>
                                <th>Erstellt</th>
                                <th>Bearbeitungszeit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.tickets.map(t => (
                                <tr key={t.id}>
                                  <td>
                                    {t.zendeskUrl ? (
                                      <a href={t.zendeskUrl} target="_blank" rel="noopener noreferrer">
                                        #{t.id}
                                      </a>
                                    ) : `#${t.id}`}
                                    <span style={{ marginLeft: 8, color: 'var(--color-text-secondary)' }}>{t.subject}</span>
                                  </td>
                                  <td>{t.produkt || '–'}</td>
                                  <td>{fmtDate(t.createdAt)}</td>
                                  <td>{t.bearbeitungFormatiert || '–'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
